import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, MessageCircle, RotateCcw, TriangleAlert } from "lucide-react";
import { waLink } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = ["40ft", "50ft", "60ft", "Multiple / Not sure"];

export default function QuoteForm({ initialSize = "" }) {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        trailer_size: initialSize || "40ft",
        from_location: "",
        to_location: "",
        message: "",
    });
    const [state, setState] = useState("idle");
    const [leadId, setLeadId] = useState("");

    useEffect(() => {
        if (initialSize) setForm((f) => ({ ...f, trailer_size: initialSize }));
    }, [initialSize]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const waText = () =>
        waLink(
            `Hello OSM Cargo Movers,\n\nTrailer Quote Request:\nName: ${form.name}\nPhone: ${form.phone}\nTrailer: ${form.trailer_size}\nFrom: ${form.from_location}\nTo: ${form.to_location}${form.message ? `\nNote: ${form.message}` : ""}`
        );

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Name and phone number are required");
            return;
        }
        setState("sending");
        try {
            const res = await fetch(`${API}/quotes`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("bad response");
            const data = await res.json();
            setLeadId(data.id);
            setState("done");
            toast.success("Enquiry received — we'll call you back shortly!");
        } catch {
            setState("error");
            toast.error("Could not save the enquiry — please send via WhatsApp instead.");
        }
    };

    const reset = () => {
        setForm({
            name: "",
            phone: "",
            trailer_size: "40ft",
            from_location: "",
            to_location: "",
            message: "",
        });
        setLeadId("");
        setState("idle");
    };

    if (state === "done") {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="card p-8 text-center sm:p-10"
                data-testid="quote-success-card"
            >
                <CheckCircle2 className="mx-auto h-14 w-14 text-[#10B981]" />
                <h3 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">
                    Enquiry Received
                </h3>
                <p className="mt-2 text-sm text-steel">
                    Reference ID: <span className="font-mono font-semibold text-ink">{leadId}</span>
                </p>
                <p className="mt-1 font-hindi text-base text-steel">
                    Thank you! Our team will contact you shortly.
                </p>
                <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                    <a
                        href={waText()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="quote-whatsapp-btn"
                        className="btn-whatsapp"
                    >
                        <MessageCircle className="h-4 w-4" />
                        Also send on WhatsApp
                    </a>
                    <button
                        onClick={reset}
                        data-testid="quote-reset-btn"
                        className="btn-outline"
                    >
                        <RotateCcw className="h-4 w-4" />
                        New enquiry
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <form onSubmit={submit} className="card p-6 sm:p-8" data-testid="quote-form">
            {state === "error" && (
                <div
                    className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                    data-testid="quote-error-alert"
                >
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                        Could not save the enquiry right now. Please send it directly on
                        WhatsApp — your details are preserved.
                        <a
                            href={waText()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 font-semibold underline"
                        >
                            Send on WhatsApp
                        </a>
                    </span>
                </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-steel">
                        Your Name *
                    </span>
                    <input
                        type="text"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Ramesh Sharma"
                        data-testid="quote-form-name"
                        className="input-field"
                    />
                </label>
                <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-steel">
                        Phone / Mobile *
                    </span>
                    <input
                        type="tel"
                        required
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="98XXXXXXXX"
                        data-testid="quote-form-phone"
                        className="input-field"
                    />
                </label>
                <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-steel">
                        Trailer Size
                    </span>
                    <select
                        value={form.trailer_size}
                        onChange={set("trailer_size")}
                        data-testid="quote-form-size"
                        className="input-field"
                    >
                        {SIZES.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-steel">
                            From
                        </span>
                        <input
                            type="text"
                            value={form.from_location}
                            onChange={set("from_location")}
                            placeholder="Mumbai"
                            data-testid="quote-form-from"
                            className="input-field"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-steel">
                            To
                        </span>
                        <input
                            type="text"
                            value={form.to_location}
                            onChange={set("to_location")}
                            placeholder="Delhi"
                            data-testid="quote-form-to"
                            className="input-field"
                        />
                    </label>
                </div>
                <label className="block sm:col-span-2">
                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-steel">
                        Cargo Details (optional)
                    </span>
                    <textarea
                        rows={3}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Steel coils, approx 24 tonne, ODC clearance not required…"
                        data-testid="quote-form-message"
                        className="input-field resize-none"
                    />
                </label>
            </div>

            <button
                type="submit"
                disabled={state === "sending"}
                data-testid="quote-form-submit-btn"
                className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-60"
            >
                {state === "sending" ? "Sending…" : "Get a Trailer Quote"}
                {state !== "sending" && <ArrowRight className="h-4 w-4" />}
            </button>
            <p className="mt-3 text-center text-xs text-fog">
                Prefer to chat?{" "}
                <a
                    href={waText()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="quote-form-inline-whatsapp"
                    className="font-semibold text-[#1FA855] underline"
                >
                    Send this enquiry on WhatsApp
                </a>
            </p>
        </form>
    );
}
